import React, { useEffect, useRef, useId } from 'react';
import { colors, spacing, borderRadius, shadows } from '../../styles/designTokens';
import Button from './Button';

/**
 * Modal Component
 * 
 * Accessible modal dialog with focus trap and proper ARIA attributes.
 * Follows WCAG 2.1 AA guidelines for modal dialogs.
 * 
 * Features:
 * - Focus trap (focus stays within modal)
 * - Escape key to close
 * - Click outside to close (optional)
 * - Proper ARIA attributes
 * - Animated entrance/exit
 * - Scrollable content
 * 
 * Usage:
 * <Modal
 *   isOpen={isOpen}
 *   onClose={handleClose}
 *   title="Confirm Action"
 *   size="md"
 * >
 *   <p>Modal content here</p>
 * </Modal>
 * 
 * Props:
 * - isOpen: Boolean - controls modal visibility
 * - onClose: Function - called when modal should close
 * - title: String - modal title (required for accessibility)
 * - size: 'sm' | 'md' | 'lg' | 'xl' | 'full' (default: 'md')
 * - closeOnClickOutside: Boolean (default: true)
 * - closeOnEscape: Boolean (default: true)
 * - showCloseButton: Boolean (default: true)
 * - children: React nodes - modal content
 */

const Modal = ({
  isOpen,
  onClose,
  title,
  size = 'md',
  closeOnClickOutside = true,
  closeOnEscape = true,
  showCloseButton = true,
  children,
  className = ''
}) => {
  const modalRef = useRef(null);
  const previousActiveElement = useRef(null);
  const titleId = useId();

  // Size configurations
  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-full mx-4'
  };

  // Focus trap effect
  useEffect(() => {
    if (isOpen && modalRef.current) {
      // Store currently focused element
      previousActiveElement.current = document.activeElement;
      
      // Focus first focusable element in modal
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }

      // Prevent body scroll
      document.body.style.overflow = 'hidden';

      return () => {
        // Restore body scroll
        document.body.style.overflow = 'unset';
        
        // Restore focus to previous element
        if (previousActiveElement.current) {
          previousActiveElement.current.focus();
        }
      };
    }
  }, [isOpen]);

  // Escape key handler
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, onClose]);

  // Focus trap handler
  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    const handleTabKey = (e) => {
      if (e.key !== 'Tab') return;

      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    };

    modalRef.current.addEventListener('keydown', handleTabKey);
    const currentRef = modalRef.current;
    
    return () => {
      if (currentRef) {
        currentRef.removeEventListener('keydown', handleTabKey);
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="presentation"
      onClick={closeOnClickOutside ? onClose : undefined}
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        animation: 'fadeIn 0.2s ease-out'
      }}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`${sizeClasses[size]} w-full ${className}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: colors.neutral[50],
          borderRadius: borderRadius.lg,
          boxShadow: shadows.lg,
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          animation: 'slideUp 0.2s ease-out'
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: spacing.lg,
            borderBottom: `1px solid ${colors.neutral[200]}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <h2
            id={titleId}
            style={{
              fontSize: '1.25rem',
              fontWeight: 600,
              color: colors.neutral[900],
              margin: 0
            }}
          >
            {title}
          </h2>
          
          {showCloseButton && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              }
              aria-label="Close modal"
            />
          )}
        </div>

        {/* Body - Scrollable */}
        <div
          style={{
            padding: spacing.lg,
            overflow: 'auto',
            flex: 1
          }}
        >
          {children}
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(20px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
    </div>
  );
};

/**
 * Modal.Footer Component
 * 
 * Standard footer for modal with action buttons
 * 
 * Usage:
 * <Modal.Footer>
 *   <Button variant="outline" onClick={onClose}>Cancel</Button>
 *   <Button variant="primary" onClick={onConfirm}>Confirm</Button>
 * </Modal.Footer>
 */
Modal.Footer = ({ children, className = '' }) => {
  return (
    <div
      className={className}
      style={{
        padding: spacing.lg,
        borderTop: `1px solid ${colors.neutral[200]}`,
        display: 'flex',
        gap: spacing.md,
        justifyContent: 'flex-end'
      }}
    >
      {children}
    </div>
  );
};

export default Modal;

